import React, {useState, useMemo, useEffect, useRef} from "react";
import {useDispatch, useSelector} from "react-redux";
import SimpleCard from "connectui/component/display/card/SimpleCard";
import {useTheme} from "connectui/theme/ThemeProvider";
import FlexContainer from "../../../../connectui/component/layout/FlexContainer";
import Text from "../../../../connectui/component/display/Text";
import Container from "../../../../connectui/component/layout/Container";
import {
  BarChart,
  BaseChartTooltip,
} from "../../../../connectui/component/charts";
import SvgIcon from "../../../../connectui/component/display/SvgIcon";
import ICONS from "../../../../connectui/icons/iconMap";
import Tooltip from "../../../../connectui/component/display/Tooltip";
import {getDashboardGuestSatisfaction} from "redux/actions/dashboardActions";
import {satisfaction} from "configuration/constants";
import DashboardEmptyStateCard from "./DashboardEmptyStateCard";
import {useTranslation} from "react-i18next";

const RATING_MAP = {
  [satisfaction.unsatisfied.value]: {x: "unsatisfied"},
  [satisfaction.somewhat_satisfied.value]: {x: "somewhat-satisfied"},
  [satisfaction.neutral.value]: {x: "neutral"},
  [satisfaction.satisfied.value]: {x: "satisfied"},
  [satisfaction.highly_satisfied.value]: {x: "highly-satisfied"},
};

const SENTIMENT_CHART_SERIES = [
  {
    id: "sentiment",
    name: "Guest Sentiment",
    dataKey: "value",
    color: "#000",
  },
];

const SENTIMENT_COLORS = {
  [satisfaction.unsatisfied.label]: "#FF5252",
  [satisfaction.somewhat_satisfied.label]: "#FF9F40",
  [satisfaction.neutral.label]: "#C6C6D9",
  [satisfaction.satisfied.label]: "#36A2EB",
  [satisfaction.highly_satisfied.label]: "#4CAF50",
};

const SentimentCard = ({timeframe, listingGroups = null}) => {
  const {theme} = useTheme();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const iconCache = useRef({});
  const {t} = useTranslation();

  const guestSatisfactionData = useSelector(
    (state) => state.dashboardReducer.guestSatisfactionData,
  );

  const SentimentCardTooltip = ({data, position, chartContainerRef}) => {
    if (!data || !data.items) return null;

    return (
      <BaseChartTooltip
        position={position}
        color={data.items[0]?.color}
        items={data.items}
        chartContainerRef={chartContainerRef}
      />
    );
  };

  const processedData = useMemo(() => {
    if (!guestSatisfactionData?.rating_range) {
      return [];
    }

    return guestSatisfactionData.rating_range.map((item) => {
      //erm
      const ratingValue = parseInt(item.key);
      const satisfactionLevel = Object.values(satisfaction).find(
        (s) => s.value === ratingValue,
      );
      const displayInfo = RATING_MAP[ratingValue];

      return {
        x: displayInfo.x,
        value: item.guest_count,
        sentiment: satisfactionLevel.label,
        color: SENTIMENT_COLORS[satisfactionLevel.label],
        iconName: displayInfo.x,
        displayX: "",
      };
    });
  }, [guestSatisfactionData]);

  useEffect(() => {
    if (!timeframe) return;

    setLoading(true);
    setError(false);

    let isMounted = true;

    dispatch(
      getDashboardGuestSatisfaction({
        start_date: timeframe.startDate,
        end_date: timeframe.endDate,
        listing_group_ids: listingGroups,
        onSuccess: () => {
          if (isMounted) setLoading(false);
        },
        onError: () => {
          if (isMounted) {
            setLoading(false);
            setError(true);
          }
        },
      }),
    );

    return () => {
      isMounted = false;
    };
  }, [timeframe?.startDate, timeframe?.endDate, listingGroups, dispatch]);

  useEffect(() => {
    processedData.forEach((item) => {
      if (item.iconName) {
        const img = new Image();
        img.onload = () => {
          iconCache.current[item.iconName] = img;
        };
        img.src = `/images/sentiment/${item.iconName}.png`;
      }
    });
  }, [processedData, guestSatisfactionData]);

  const renderSentimentIcon = (ctx, item, x, y, currentTheme) => {
    if (item.iconName) {
      const iconImg = iconCache.current[item.iconName];
      if (iconImg) {
        const iconSize = 20;

        ctx.save();

        ctx.beginPath();
        ctx.arc(x, y, 12, 0, Math.PI * 2);
        ctx.closePath();
        ctx.clip();

        ctx.drawImage(
          iconImg,
          x - iconSize / 2,
          y - iconSize / 2,
          iconSize,
          iconSize,
        );

        ctx.globalCompositeOperation = "source-atop";
        ctx.fillStyle = item.color;
        ctx.fillRect(x - iconSize / 2, y - iconSize / 2, iconSize, iconSize);

        ctx.restore();
      }
    }
  };

  const ticks = useMemo(() => {
    if (!processedData.length) return [0, 25, 50, 75];

    const maxValue = Math.max(...processedData.map((item) => item.value));
    const roundedMax = Math.ceil(maxValue / 100) * 100;
    const step = roundedMax / 3;

    return Array.from({length: 4}, (_, i) => Math.round(i * step));
  }, [processedData]);

  return (
    <SimpleCard
      customContent={
        <FlexContainer
          width={"100%"}
          alignItems={"flex-start"}
          justifyContent={"flex-start"}
          flexDirection={"column"}
          gap={theme.spacing(2)}
        >
          <FlexContainer
            justifyContent={"space-between"}
            alignItems={"center"}
            width={"100%"}
          >
            <FlexContainer alignItems="center" gap={theme.spacing(1)}>
              <Text
                variant={"textMD"}
                color={"textSecondary"}
                weight={"medium"}
              >
                {t("dashboard.guest-sentiment.title")}
              </Text>
              <Tooltip tooltipContent={t("dashboard.guest-sentiment.help")}>
                <Container sx={{cursor: "help"}}>
                  <SvgIcon
                    component={ICONS["help-circle"]}
                    sx={{
                      width: "14px",
                      height: "14px",
                      color: theme.colors.gray400,
                    }}
                  />
                </Container>
              </Tooltip>
            </FlexContainer>
          </FlexContainer>

          <Container
            height={200}
            width="100%"
            position="relative"
            skeleton={{enabled: loading, width: "100%", height: "200px"}}
          >
            {!loading && !error && processedData.length > 0 ? (
              <BarChart
                data={processedData}
                series={SENTIMENT_CHART_SERIES}
                settings={{
                  height: 200,
                  tooltip: {
                    component: SentimentCardTooltip,
                  },
                  grid: {
                    horizontal: true,
                    vertical: false,
                    color: theme.colors.gray300,
                  },
                  axis: {
                    x: {
                      tickCount: processedData.length,
                      visible: false,
                    },
                    y: {
                      tickCount: ticks.length,
                      customTicks: ticks,
                      domain: [0, ticks[ticks.length - 1]],
                    },
                  },
                  padding: {
                    top: 20,
                    right: 20,
                    bottom: 60,
                    left: 40,
                  },
                  labels: {
                    top: {
                      dataKey: "value",
                      fontSize: 14,
                      textColor: theme.colors.gray700,
                      marginY: 8,
                      formatter: (value) => value.toString(),
                    },
                    bottom: {
                      renderer: renderSentimentIcon,
                    },
                  },
                }}
                key="sentiment-bar-chart"
              />
            ) : !loading && (error || processedData.length === 0) ? (
              <DashboardEmptyStateCard
                text={t("dashboard.guest-sentiment.empty-state")}
                icon={ICONS["no-messages-white"]}
                height="200px"
              />
            ) : null}
          </Container>
        </FlexContainer>
      }
      sx={{
        height: "100%",
        width: "100%",
        backgroundColor: theme.colors.white,
      }}
    />
  );
};

export default SentimentCard;
